import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Gem, LogOut, Home, ShoppingBag, PenTool, ShieldCheck, Store, HelpCircle, Phone, MessageCircle } from "lucide-react";
import {
  SUPPORT_PHONE_E164, SUPPORT_PHONE_DISPLAY, SUPPORT_WHATSAPP_URL, SUPPORT_HOURS,
} from "@/lib/support";

/** Roles the user can access from the current auth object.
 *  Returns an array of {key, label, icon, path, testid}. */
function accessibleRoles(user) {
  const roles = [];
  if (!user) return roles;
  const r = user.role;
  if (r === "buyer" || r === "both") roles.push({ key: "buyer", label: "Buyer Dashboard", shortLabel: "Buyer", icon: ShoppingBag, path: "/buyer", testid: "switch-buyer" });
  if (r === "seller" || r === "both") roles.push({ key: "seller", label: "Seller Dashboard", shortLabel: "Seller", icon: PenTool, path: "/seller", testid: "switch-seller" });
  if (r === "admin" || r === "employee") roles.push({ key: "admin", label: "Admin", shortLabel: "Admin", icon: ShieldCheck, path: "/admin", testid: "switch-admin" });
  return roles;
}

/** Map the current dashboard path to the tour that should replay. */
function tourRoleFromPath(pathname) {
  if (pathname.startsWith("/vendor")) return "cam_vendor";
  if (pathname.startsWith("/seller")) return "seller";
  return "buyer";  // default fallback for /buyer and anything else
}

export function DashShell({ title, subtitle, nav, active, onTab, children, currentRole }) {
  const { user, logout, becomeSeller } = useAuth();
  const navigate = useNavigate();
  const roles = accessibleRoles(user);
  const showSwitcher = roles.length > 1;
  const canBecomeSeller = user?.role === "buyer";
  const isStaff = user?.role === "admin" || user?.role === "employee";
  return (
    <div className="min-h-screen flex bg-background">
      <aside className="w-60 shrink-0 border-r border-border bg-card hidden md:flex flex-col fixed h-screen">
        <Link to="/" className="flex items-center gap-2 px-5 h-16 border-b border-border">
          <Gem className="h-6 w-6 text-primary" strokeWidth={1.5} />
          <span className="font-display font-extrabold text-lg">Jewel<span className="text-primary">tix</span></span>
        </Link>
        <nav className="flex-1 p-3 space-y-1 overflow-auto">
          {nav.map((n) => (
            <button
              key={n.key}
              data-testid={`tab-${n.key}`}
              onClick={() => onTab(n.key)}
              className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                active === n.key ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary hover:text-foreground"
              }`}
            >
              <n.icon className="h-4 w-4" /> {n.label}
              {n.badge > 0 && <span className={`ml-auto text-[10px] font-mono px-1.5 rounded-full ${active === n.key ? "bg-white/20" : "bg-primary text-primary-foreground"}`}>{n.badge}</span>}
            </button>
          ))}
        </nav>
        <div className="p-3 border-t border-border space-y-1">
          <button onClick={() => navigate("/")} className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm text-muted-foreground hover:bg-secondary"><Home className="h-4 w-4" /> Storefront</button>
          <a
            href={`tel:${SUPPORT_PHONE_E164}`}
            data-testid="dash-support-call"
            className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm text-muted-foreground hover:bg-secondary"
          >
            <Phone className="h-4 w-4" /> {SUPPORT_PHONE_DISPLAY}
          </a>
          <a
            href={SUPPORT_WHATSAPP_URL}
            target="_blank"
            rel="noreferrer"
            data-testid="dash-support-whatsapp"
            className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm text-muted-foreground hover:bg-secondary"
          >
            <MessageCircle className="h-4 w-4 text-emerald-600" /> WhatsApp us
          </a>
          <p className="px-3 text-[10px] font-mono text-muted-foreground">{SUPPORT_HOURS}</p>
          <button onClick={() => { logout(); navigate("/"); }} data-testid="dash-logout" className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm text-muted-foreground hover:bg-secondary"><LogOut className="h-4 w-4" /> Log out</button>
        </div>
      </aside>

      <div className="flex-1 min-w-0 md:ml-60">
        <div className="sticky top-0 z-30 h-16 border-b border-border bg-card px-5 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <h1 className="font-display font-bold text-lg leading-tight truncate">{title}</h1>
            {subtitle && <p className="text-xs text-muted-foreground truncate">{subtitle}</p>}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {/* --- Shop Now — always visible, goes to the marketplace --- */}
            <button
              onClick={() => navigate("/marketplace")}
              data-testid="dash-shop-now"
              className="hidden sm:inline-flex items-center gap-1.5 rounded-full bg-primary text-primary-foreground text-xs font-semibold px-3 py-1.5 hover:bg-primary/90 transition-colors shadow-sm"
              aria-label="Shop Now"
            >
              <Store className="h-3.5 w-3.5" />
              <span className="hidden md:inline">Shop Now</span>
            </button>
            {/* --- Role switcher: appears when the user has access to 2+ dashboards --- */}
            {showSwitcher && (
              <div className="hidden sm:flex items-center gap-1 rounded-full border border-border bg-background/80 backdrop-blur px-1 py-1" data-testid="dash-role-switcher">
                {roles.map((r) => {
                  const isActive = (currentRole || active?.split?.("-")?.[0]) === r.key || window.location.pathname.startsWith(r.path);
                  return (
                    <button
                      key={r.key}
                      data-testid={r.testid}
                      onClick={() => !isActive && navigate(r.path)}
                      className={`flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold rounded-full transition-colors ${
                        isActive ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground hover:bg-secondary"
                      }`}
                      aria-current={isActive ? "page" : undefined}
                    >
                      <r.icon className="h-3.5 w-3.5" />
                      <span className="hidden md:inline">{r.label}</span>
                      <span className="md:hidden">{r.shortLabel}</span>
                    </button>
                  );
                })}
              </div>
            )}
            {canBecomeSeller && (
              <button
                onClick={async () => { await becomeSeller(); navigate("/seller"); }}
                data-testid="dash-become-seller"
                className="hidden md:inline-flex items-center gap-1.5 rounded-full border border-primary/40 text-primary text-xs font-semibold px-3 py-1.5 hover:bg-primary/10 transition-colors"
              >
                <PenTool className="h-3.5 w-3.5" /> Start selling
              </button>
            )}
            {!isStaff && (
              <button
                onClick={() => navigate(`${window.location.pathname}?tour=${tourRoleFromPath(window.location.pathname)}`)}
                data-testid="dash-take-tour"
                title="Replay the guided tour"
                className="hidden sm:inline-flex items-center gap-1 rounded-full border border-border text-muted-foreground hover:text-primary hover:border-primary text-xs font-semibold px-2.5 py-1.5 transition-colors"
                aria-label="Replay tour"
              >
                <HelpCircle className="h-3.5 w-3.5" />
                <span className="hidden md:inline">Tour</span>
              </button>
            )}
            <div className="h-8 w-8 rounded-full bg-primary/15 text-primary flex items-center justify-center text-sm font-bold">{user?.name?.[0]}</div>
            <div className="hidden lg:block text-right"><p className="text-sm font-medium leading-tight">{user?.name}</p><p className="text-[10px] uppercase tracking-wide text-muted-foreground">{user?.role}</p></div>
          </div>
        </div>
        {/* Mobile role switcher — full-width chips just below the top bar so it's always thumb-reachable. Includes a primary Shop Now chip so buyers reach the storefront in one tap. */}
        <div className="sm:hidden flex gap-1 px-3 py-2 border-b border-border bg-card overflow-x-auto scroll-fade" data-testid="dash-mobile-actions">
          <button
            onClick={() => navigate("/marketplace")}
            data-testid="dash-shop-now-mobile"
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-full whitespace-nowrap bg-primary text-primary-foreground shadow-sm"
          >
            <Store className="h-3.5 w-3.5" /> Shop Now
          </button>
          {showSwitcher && roles.map((r) => {
            const isActive = window.location.pathname.startsWith(r.path);
            return (
              <button
                key={r.key}
                data-testid={r.testid + "-mobile"}
                onClick={() => !isActive && navigate(r.path)}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-full whitespace-nowrap ${
                  isActive ? "bg-secondary text-foreground border border-border" : "border border-border text-muted-foreground"
                }`}
              >
                <r.icon className="h-3.5 w-3.5" /> {r.shortLabel}
              </button>
            );
          })}
          <button
            onClick={() => navigate(`${window.location.pathname}?tour=${tourRoleFromPath(window.location.pathname)}`)}
            data-testid="dash-take-tour-mobile"
            className={`${isStaff ? "hidden " : ""}flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-full whitespace-nowrap border border-border text-muted-foreground`}
          >
            <HelpCircle className="h-3.5 w-3.5" /> Tour
          </button>
        </div>
        <div className="md:hidden flex overflow-x-auto scroll-fade border-b border-border bg-card px-2">
          {nav.map((n) => (
            <button key={n.key} onClick={() => onTab(n.key)} className={`px-3 py-2.5 text-xs font-medium whitespace-nowrap ${active === n.key ? "text-primary border-b-2 border-primary" : "text-muted-foreground"}`}>{n.label}</button>
          ))}
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

export function KPI({ label, value, sub, accent }) {
  return (
    <div className="border border-border rounded-md bg-card p-4">
      <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</p>
      <p className={`font-display font-bold text-2xl mt-1 ${accent || ""}`}>{value}</p>
      {sub && <p className="text-xs text-muted-foreground mt-0.5 font-mono">{sub}</p>}
    </div>
  );
}

const TAG_STYLES = {
  pending: "bg-amber-500/10 text-amber-600 border-amber-500/30",
  needs_photos: "bg-orange-500/10 text-orange-600 border-orange-500/30",
  available: "bg-emerald-500/10 text-emerald-600 border-emerald-500/30",
  completed: "bg-emerald-500/10 text-emerald-600 border-emerald-500/30",
  paid: "bg-emerald-500/10 text-emerald-600 border-emerald-500/30",
  approved: "bg-emerald-500/10 text-emerald-600 border-emerald-500/30",
  frozen: "bg-red-500/10 text-red-600 border-red-500/30",
  disputed: "bg-red-500/10 text-red-600 border-red-500/30",
  refunded: "bg-slate-500/10 text-slate-600 border-slate-500/30",
  rejected: "bg-red-500/10 text-red-600 border-red-500/30",
  open: "bg-amber-500/10 text-amber-600 border-amber-500/30",
  under_review: "bg-blue-500/10 text-blue-600 border-blue-500/30",
  resolved: "bg-emerald-500/10 text-emerald-600 border-emerald-500/30",
  requested: "bg-amber-500/10 text-amber-600 border-amber-500/30",
};

export function StatusTag({ status }) {
  const s = TAG_STYLES[status] || "bg-secondary text-muted-foreground border-border";
  return <span className={`font-mono text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ${s}`}>{String(status).replace("_", " ")}</span>;
}
